import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Logo } from '../components/Logo';
import { Watermark } from '../components/ui';

export function LoginPage() {
  const { me, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const server = useServerWakeUp();

  if (me) return <Navigate to={me.role === 'admin' ? '/admin' : '/painel'} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Informe seu e-mail e sua senha.');
      return;
    }
    setBusy(true);
    try {
      const user = await login(email.trim(), password);
      const from = (location.state as { from?: string } | null)?.from;
      const home = user.role === 'admin' ? '/admin' : '/painel';
      if (user.mustChangePassword) navigate('/conta', { replace: true });
      else navigate(from && from.startsWith(home) ? from : home, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <aside className="login__brand">
        <Watermark className="login__wm" />
        <Logo variant="vertical" className="login__logo" />
        <div className="stack" style={{ gap: 10 }}>
          <p className="eyebrow" style={{ color: 'var(--pixel-green)' }}>
            &lt;_Pixerito_Social/&gt;
          </p>
          <p className="login__claim">
            Acompanhe o que a Pixerito <span style={{ color: 'var(--pixel-green)' }}>faz pelo seu negócio</span>, mês a mês.
          </p>
          <p className="login__small">Redes sociais, site, Google Meu Negócio, identidade visual e vídeo em um só painel.</p>
        </div>
      </aside>

      <main className="login__main">
        <form className="login__form" onSubmit={submit} noValidate>
          <div className="stack" style={{ gap: 6 }}>
            <p className="label">Beta fechado</p>
            <h1 className="t-h2">Entrar no painel</h1>
            <p className="muted t-small">Use o e-mail e a senha que a equipe Pixerito enviou para você.</p>
          </div>

          {server === 'waking' && (
            <div className="notice" role="status">
              <span className="loader" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <span className="t-small">Acordando o servidor. Na primeira visita do dia isso leva até um minuto.</span>
            </div>
          )}
          {error && (
            <div className="notice notice--error" role="alert">
              {error}
            </div>
          )}

          <div className="field">
            <label htmlFor="login-email">E-mail</label>
            <input
              id="login-email"
              className="input"
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@suaempresa.com.br"
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="login-password">Senha</label>
            <input
              id="login-password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Sua senha"
            />
          </div>

          <button className="btn btn--action btn--block" type="submit" disabled={busy}>
            {busy ? 'Entrando…' : 'Entrar'}
          </button>
          <p className="t-caption">Esqueceu a senha? Fale com a equipe Pixerito pelo WhatsApp que ela cria uma nova para você.</p>
        </form>
      </main>
    </div>
  );
}

/**
 * No plano gratuito a API dorme depois de 15 minutos sem uso. Ao abrir o login já pedimos para ela
 * acordar e, se demorar, avisamos a pessoa em vez de deixar o botão "travado".
 */
function useServerWakeUp() {
  const [state, setState] = useState<'checking' | 'waking' | 'ready'>('checking');
  useEffect(() => {
    let done = false;
    const slow = window.setTimeout(() => {
      if (!done) setState('waking');
    }, 1500);
    api.ping().finally(() => {
      done = true;
      window.clearTimeout(slow);
      setState('ready');
    });
    return () => {
      done = true;
      window.clearTimeout(slow);
    };
  }, []);
  return state;
}
